import React from "react";

export default function JobCommentsApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Jobs API — Job Comments
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Fetch comments for a job and create new job comments. This endpoint
            requires authentication and applies{" "}
            <span className="font-semibold">role-based visibility</span>.
            Customer users only receive public comments, while internal users
            can access all comments.
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Methods
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                GET, POST
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/job-comments/{`{jobid}`}/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            This endpoint supports two main actions:
          </p>

          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              <span className="font-semibold">Fetch comments</span> for a job
              with pagination.
            </li>
            <li>
              <span className="font-semibold">Create a new comment</span>.
            </li>
            <li>
              Internal users can generally see all comments; customer users only
              see public comments.
            </li>
            <li>
              Fetching comments also updates the user's last time checked
              comments for that job.
            </li>
          </ul>
        </section>

        {/* Fetch comments */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Fetch job comments
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">HTTP</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-semibold">Method:</span>{" "}
                <code className="font-mono">GET</code>
              </li>
              <li>
                <span className="font-semibold">URL:</span>{" "}
                <code className="font-mono">
                  /api/job-comments/{`{jobid}`}/
                </code>
              </li>
              <li>
                <span className="font-semibold">Headers:</span>
                <br />
                <code className="font-mono">Authorization: JWT your_token</code>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Path parameters
            </div>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
              <li>
                <code className="font-mono">jobid</code> —{" "}
                <span className="font-mono">number</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  required
                </span>{" "}
                Job ID to fetch comments for.
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Visibility rules
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                Superusers, staff, Project Managers, Internal Coordinators, and
                Account Managers can view job comments.
              </li>
              <li>
                Customer users can view comments only for jobs belonging to
                their customer (including extra customers via{" "}
                <code className="font-mono">UserCustomer</code> mapping).
              </li>
              <li>
                Portside Integration users can view comments only for jobs
                created by Portside.
              </li>
            </ul>
          </div>

          {/* Example: GET cURL */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  GET
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/job-comments/{`{jobid}`}/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`curl -X GET https://api.livetakeoff.com/api/job-comments/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN"`}
                </pre>
              </div>
            </div>
          </div>

          {/* Example response */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              200 OK (paginated response)
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              The endpoint uses pagination and returns a list of comments.
            </p>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "count": 2,
  "next": null,
  "previous": null,
  "results": [
    {
      "id": 101,
      "comment": "The team is on site.",
      "is_public": true,
      "created": "03/08/2026 10:30 AM",
      "author": {
        "id": 5,
        "first_name": "John",
        "last_name": "Smith"
      }
    },
    {
      "id": 102,
      "comment": "Waiting on access clearance.",
      "is_public": false,
      "created": "03/08/2026 11:15 AM",
      "author": {
        "id": 7,
        "first_name": "Mary",
        "last_name": "Jones"
      }
    }
  ]
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Create comment */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Create a job comment
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">HTTP</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-semibold">Method:</span>{" "}
                <code className="font-mono">POST</code>
              </li>
              <li>
                <span className="font-semibold">URL:</span>{" "}
                <code className="font-mono">
                  /api/job-comments/{`{jobid}`}/
                </code>
              </li>
              <li>
                <span className="font-semibold">Headers:</span>
                <br />
                <code className="font-mono">Authorization: JWT your_token</code>
                <br />
                <code className="font-mono">
                  Content-Type: application/json
                </code>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Body fields
            </div>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
              <li>
                <code className="font-mono">comment</code> —{" "}
                <span className="font-mono">string</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  required
                </span>
              </li>
              {/* <li>
                <code className="font-mono">sendSMS</code> —{" "}
                <span className="font-mono">boolean</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>{" "}
                Send text notification to assigned project managers.
              </li>
              <li>
                <code className="font-mono">sendEmail</code> —{" "}
                <span className="font-mono">boolean</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li>
              <li>
                <code className="font-mono">sendEmailToProjectManager</code> —{" "}
                <span className="font-mono">boolean</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li>
              <li>
                <code className="font-mono">sendEmailToFollowers</code> —{" "}
                <span className="font-mono">boolean</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li>
              <li>
                <code className="font-mono">isPublic</code> —{" "}
                <span className="font-mono">boolean</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>{" "}
                Customer comments are automatically forced to public.
              </li>
              <li>
                <code className="font-mono">emails</code> —{" "}
                <span className="font-mono">array[string]</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li>
              <li>
                <code className="font-mono">projectManagerEmails</code> —{" "}
                <span className="font-mono">array[string]</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li>
              <li>
                <code className="font-mono">followerEmails</code> —{" "}
                <span className="font-mono">array[string]</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  optional
                </span>
              </li> */}
              <li>
                <code className="font-mono">first_name</code>,{" "}
                <code className="font-mono">last_name</code>,{" "}
                <code className="font-mono">email</code> —{" "}
                <span className="font-mono">string</span>
                <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                  required for Portside Integration users
                </span>
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Important behavior
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-amber-900 marker:text-amber-400">
              <li>
                Customer users automatically create{" "}
                <code className="font-mono">public</code> comments.
              </li>
              <li>
                External vendor project managers trigger an automatic admin
                email notification when they add a comment.
              </li>
              <li>
                Customer users also trigger an automatic admin email
                notification when they add a comment.
              </li>
              <li>
                Portside Integration users must provide{" "}
                <code className="font-mono">first_name</code>,{" "}
                <code className="font-mono">last_name</code>, and{" "}
                <code className="font-mono">email</code>.
              </li>
            </ul>
          </div>

          {/* Example: POST cURL */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/job-comments/{`{jobid}`}/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`curl -X POST https://api.livetakeoff.com/api/job-comments/9876/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "comment": "The aircraft is now ready for the next step.",
    "first_name": "dispatcher first name",
    "last_name": "dispatcher last name",
    "email": "dispatcher@example.com",
  }'`}
                </pre>
              </div>
            </div>
          </div>

          {/* Example: fetch */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/{`{jobid}`}/comments/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`const res = await fetch("https://api.livetakeoff.com/api/job-comments/9876/", {
  method: "POST",
  headers: {
    Authorization: "JWT " + token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    comment: "The aircraft is now ready for the next step.",
    first_name: "dispatcher first name",
    last_name: "dispatcher last name",
    email: "dispatcher@example.com",
    emails: ["ops@company.com"]
  })
});

if (!res.ok) throw new Error("Failed to create comment");
const data = await res.json();`}
                </pre>
              </div>
            </div>
          </div>

          {/* Example response */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              201 Created
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              On success, returns the newly created comment object.
            </p>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "id": 205,
  "comment": "The aircraft is now ready for the next step.",
  "is_public": true,
  "created": "03/08/2026 02:15 PM",
  "author": {
    "id": 12,
    "first_name": "Enrique",
    "last_name": "Delgado"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notifications / behavior */}
        {/* <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Notifications & side effects
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <ul className="grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                <code className="font-mono">sendSMS=true</code> sends SMS to
                assigned project managers.
              </li>
              <li>
                <code className="font-mono">sendEmail=true</code> sends email to
                the addresses provided in{" "}
                <code className="font-mono">emails</code>.
              </li>
              <li>
                <code className="font-mono">
                  sendEmailToProjectManager=true
                </code>{" "}
                sends email to the addresses provided in{" "}
                <code className="font-mono">projectManagerEmails</code>.
              </li>
              <li>
                <code className="font-mono">sendEmailToFollowers=true</code>{" "}
                sends email to the addresses provided in{" "}
                <code className="font-mono">followerEmails</code>.
              </li>
            </ul>
          </div>
        </section> */}

        {/* Errors */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Errors
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <ul className="space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-mono">403</span> — you do not have
                permission to view comments for this job or create a comment.
              </li>
              <li>
                <span className="font-mono">400</span> — missing required
                Portside Integration fields (
                <code className="font-mono">first_name</code>,{" "}
                <code className="font-mono">last_name</code>,{" "}
                <code className="font-mono">email</code>).
              </li>
            </ul>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "error": "first_name, last_name and email are required for Portside Integration users"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Permissions & rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Permissions & rules
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              Who can view or create comments?
            </div>
            <ul className="mt-2 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>Superuser / staff users.</li>
              <li>Project Managers.</li>
              <li>Internal Coordinators.</li>
              <li>Account Managers.</li>
              <li>
                Customer users matching the job customer (including extra
                customer mappings).
              </li>
              <li>
                Portside Integration users only for Jobs created by Portside.
              </li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}
